import type { Metatags, PossibleMetadata } from "shared/util/metadata";
import { getOptionalLink, getProps, isDefined, notNullish } from "shelving";
import { joinCSP, mergeTitles } from "shared/util/metadata";

/**
 * Component that generates a full <html> page (for serverside rendering).
 * `title` is the only required property from Config (status is also required but we Exclude<> it above).
 */
export function HTML({ children, meta, links, styles, modules, scripts, site, title, description, language, image, csp, base, path }: { children?: React.ReactNode } & PossibleMetadata): React.ReactElement {
	const canonical = getOptionalLink(path, base);
	return (
		<html lang={language || undefined}>
			<head>
				<title>{title && site ? mergeTitles(title, site) : title || site}</title>
				{getProps<Metatags>({
					"viewport": "initial-scale=1, width=device-width, viewport-fit=cover, user-scalable=no",
					"msapplication-tap-highlight": "no",
					"format-detection": "telephone=no",
					"description": description,
					"canonical": canonical,
					"og:url": canonical,
					"og:title": title,
					"og:site_name": site,
					"og:locale": language,
					"og:image": image,
					"twitter:card": image ? "summary_large_image" : null,
					"Content-Security-Policy": joinCSP(csp),
					...meta,
				})
					.map(([k, x]) => {
						if (notNullish(x)) {
							const y = x === true ? "yes" : x === false ? "no" : x;
							if (k.startsWith("og:")) return <meta key={k} property={k} content={y} />; // Tags that start with `og:` use `property=""`
							if (k.match(R_HTTP_EQUIV)) return <meta key={k} httpEquiv={k} content={y} />; // Tags that are in `Snake-Case` use `http-equiv=""`
							return <meta key={k} name={k} content={y} />; // All other tags use `content=""`
						}
					})
					.filter(isDefined)}
				{links && getProps(links).map(([k, v]) => <link key={k} rel={k} href={v} type={v.endsWith(".png") ? "image/png" : v.endsWith(".ico") ? "image/x-icon" : undefined} />)}
				{styles && styles.map(v => <link key={v} rel="stylesheet" type="text/css" href={v} />)}
				{modules && modules.map(v => <script key={v} type="module" src={v} defer />)}
				{scripts && scripts.map(v => <script key={v} type="text/javascript" src={v} defer />)}
			</head>
			<body>{children}</body>
		</html>
	);
}

/** Meta tags with a capital first letter and hyphens, e.g. `Content-Security-Policy` or `Accept`, are `http-equiv=""` tags. */
const R_HTTP_EQUIV = /^[A-Z][a-zA-Z0-9]*(-[A-Z][a-zA-Z0-9]*)*$/;
